import React, { useEffect, useMemo, useState } from "react";
import { Select, SelectItem, Tabs, Tab } from "@heroui/react";

/**
 * GeneralMeetingDateInput
 *
 * Captures the date of a company's annual general meeting in two flexible
 * formats while persisting a structured JSON string:
 *
 *   { type: "day",     day: 14, month: 5 }
 *   { type: "weekday", ordinal: 1, weekday: 5, month: 7 }
 *
 * Legacy values stored as "DD-MM" are interpreted as { type: "day" } so older
 * records keep rendering correctly.
 */

export type GeneralMeetingDateValue =
    | { type: "day"; day: number; month: number }
    | {
          type: "weekday";
          ordinal: number; // 1..5 or -1 for "laatste"
          weekday: number; // 1..7  (1 = maandag, 7 = zondag)
          month: number;
      };

interface GeneralMeetingDateInputProps {
    value: string;
    onChange: (value: string) => void;
    isDisabled?: boolean;
    label?: string;
}

const MONTHS: { value: number; label: string }[] = [
    { value: 1, label: "januari" },
    { value: 2, label: "februari" },
    { value: 3, label: "maart" },
    { value: 4, label: "april" },
    { value: 5, label: "mei" },
    { value: 6, label: "juni" },
    { value: 7, label: "juli" },
    { value: 8, label: "augustus" },
    { value: 9, label: "september" },
    { value: 10, label: "oktober" },
    { value: 11, label: "november" },
    { value: 12, label: "december" },
];

const WEEKDAYS: { value: number; label: string }[] = [
    { value: 1, label: "maandag" },
    { value: 2, label: "dinsdag" },
    { value: 3, label: "woensdag" },
    { value: 4, label: "donderdag" },
    { value: 5, label: "vrijdag" },
    { value: 6, label: "zaterdag" },
    { value: 7, label: "zondag" },
];

const ORDINALS: { value: number; label: string }[] = [
    { value: 1, label: "1ste" },
    { value: 2, label: "2de" },
    { value: 3, label: "3de" },
    { value: 4, label: "4de" },
    { value: 5, label: "5de" },
    { value: -1, label: "laatste" },
];

const ordinalDayLabel = (n: number) => {
    if (n === 1) return "1ste";
    if (n === 2) return "2de";
    if (n === 3) return "3de";
    return `${n}de`;
};

export const formatGeneralMeetingDate = (raw?: string | null): string => {
    if (!raw) return "";
    const parsed = parseGeneralMeetingDate(raw);
    if (!parsed) return raw;
    if (parsed.type === "day") {
        const month = MONTHS.find((m) => m.value === parsed.month);
        if (!month) return raw;
        return `${ordinalDayLabel(parsed.day)} dag van de maand ${month.label}`;
    }
    const ord = ORDINALS.find((o) => o.value === parsed.ordinal);
    const wd = WEEKDAYS.find((w) => w.value === parsed.weekday);
    const month = MONTHS.find((m) => m.value === parsed.month);
    if (!ord || !wd || !month) return raw;
    return `${ord.label} ${wd.label} van de maand ${month.label}`;
};

export const parseGeneralMeetingDate = (
    raw?: string | null,
): GeneralMeetingDateValue | null => {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;

    // JSON-serialized structured value (current format)
    if (trimmed.startsWith("{")) {
        try {
            const obj = JSON.parse(trimmed);
            if (obj?.type === "day") {
                const day = Number(obj.day);
                const month = Number(obj.month);
                if (
                    Number.isInteger(day) &&
                    day >= 1 &&
                    day <= 31 &&
                    Number.isInteger(month) &&
                    month >= 1 &&
                    month <= 12
                ) {
                    return { type: "day", day, month };
                }
            }
            if (obj?.type === "weekday") {
                const ordinal = Number(obj.ordinal);
                const weekday = Number(obj.weekday);
                const month = Number(obj.month);
                const ordinalOk =
                    ordinal === -1 || (Number.isInteger(ordinal) && ordinal >= 1 && ordinal <= 5);
                if (
                    ordinalOk &&
                    Number.isInteger(weekday) &&
                    weekday >= 1 &&
                    weekday <= 7 &&
                    Number.isInteger(month) &&
                    month >= 1 &&
                    month <= 12
                ) {
                    return { type: "weekday", ordinal, weekday, month };
                }
            }
        } catch {
            // fall through to legacy parsing
        }
    }

    // Legacy "DD-MM" string
    const match = /^(\d{1,2})-(\d{1,2})$/.exec(trimmed);
    if (match) {
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10);
        if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
            return { type: "day", day, month };
        }
    }

    return null;
};

const serialize = (val: GeneralMeetingDateValue): string => JSON.stringify(val);

const GeneralMeetingDateInput: React.FC<GeneralMeetingDateInputProps> = ({
    value,
    onChange,
    isDisabled = false,
    label = "Datum algemene vergadering",
}) => {
    const initial = useMemo(() => parseGeneralMeetingDate(value), [value]);
    const [mode, setMode] = useState<"day" | "weekday">(
        initial?.type === "weekday" ? "weekday" : "day",
    );

    const [day, setDay] = useState<number | null>(
        initial?.type === "day" ? initial.day : null,
    );
    const [dayMonth, setDayMonth] = useState<number | null>(
        initial?.type === "day" ? initial.month : null,
    );

    const [ordinal, setOrdinal] = useState<number | null>(
        initial?.type === "weekday" ? initial.ordinal : null,
    );
    const [weekday, setWeekday] = useState<number | null>(
        initial?.type === "weekday" ? initial.weekday : null,
    );
    const [weekdayMonth, setWeekdayMonth] = useState<number | null>(
        initial?.type === "weekday" ? initial.month : null,
    );

    // If incoming value changes (e.g., parent reload), sync state
    useEffect(() => {
        const parsed = parseGeneralMeetingDate(value);
        if (!parsed) return;
        if (parsed.type === "day") {
            setMode("day");
            setDay(parsed.day);
            setDayMonth(parsed.month);
        } else {
            setMode("weekday");
            setOrdinal(parsed.ordinal);
            setWeekday(parsed.weekday);
            setWeekdayMonth(parsed.month);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const emit = (next: GeneralMeetingDateValue | null) => {
        onChange(next ? serialize(next) : "");
    };

    const handleDayChange = (nextDay: number | null, nextMonth: number | null) => {
        setDay(nextDay);
        setDayMonth(nextMonth);
        if (nextDay && nextMonth) {
            emit({ type: "day", day: nextDay, month: nextMonth });
        } else {
            emit(null);
        }
    };

    const handleWeekdayChange = (
        nextOrd: number | null,
        nextWd: number | null,
        nextMonth: number | null,
    ) => {
        setOrdinal(nextOrd);
        setWeekday(nextWd);
        setWeekdayMonth(nextMonth);
        if (nextOrd !== null && nextWd && nextMonth) {
            emit({
                type: "weekday",
                ordinal: nextOrd,
                weekday: nextWd,
                month: nextMonth,
            });
        } else {
            emit(null);
        }
    };

    const preview = useMemo(() => {
        if (mode === "day" && day && dayMonth) {
            const month = MONTHS.find((m) => m.value === dayMonth)!;
            return `${ordinalDayLabel(day)} dag van de maand ${month.label}`;
        }
        if (
            mode === "weekday" &&
            ordinal !== null &&
            weekday &&
            weekdayMonth
        ) {
            const ord = ORDINALS.find((o) => o.value === ordinal)!;
            const wd = WEEKDAYS.find((w) => w.value === weekday)!;
            const month = MONTHS.find((m) => m.value === weekdayMonth)!;
            return `${ord.label} ${wd.label} van de maand ${month.label}`;
        }
        return "";
    }, [mode, day, dayMonth, ordinal, weekday, weekdayMonth]);

    const dayOptions = useMemo(
        () =>
            Array.from({ length: 31 }, (_, i) => ({
                value: i + 1,
                label: ordinalDayLabel(i + 1),
            })),
        [],
    );

    const handleModeChange = (next: "day" | "weekday") => {
        setMode(next);
        if (next === "day") {
            if (day && dayMonth) {
                emit({ type: "day", day, month: dayMonth });
            } else {
                emit(null);
            }
        } else {
            if (ordinal !== null && weekday && weekdayMonth) {
                emit({
                    type: "weekday",
                    ordinal,
                    weekday,
                    month: weekdayMonth,
                });
            } else {
                emit(null);
            }
        }
    };

    return (
        <div className="flex flex-col gap-2">
            <span className="text-foreground text-xs font-medium">{label}</span>

            <Tabs
                size="sm"
                aria-label="Datumtype"
                selectedKey={mode}
                onSelectionChange={(key) => handleModeChange(key as "day" | "weekday")}
                isDisabled={isDisabled}
                classNames={{
                    tabList: "rounded-md bg-neutral-100 p-1",
                    cursor: "bg-white shadow-sm",
                    tab: "h-7 px-3 text-xs",
                }}
            >
                <Tab key="day" title="Specifieke dag" />
                <Tab key="weekday" title="Weekdag in maand" />
            </Tabs>

            {mode === "day" ? (
                <div className="grid grid-cols-2 gap-2">
                    <Select
                        size="sm"
                        label="Dag"
                        placeholder="Kies dag"
                        selectedKeys={day ? [day.toString()] : []}
                        onChange={(e) =>
                            handleDayChange(
                                e.target.value ? parseInt(e.target.value, 10) : null,
                                dayMonth,
                            )
                        }
                        isDisabled={isDisabled}
                        variant="bordered"
                        classNames={{ label: "text-xs", value: "text-sm" }}
                    >
                        {dayOptions.map((d) => (
                            <SelectItem key={d.value.toString()}>{d.label}</SelectItem>
                        ))}
                    </Select>
                    <Select
                        size="sm"
                        label="Maand"
                        placeholder="Kies maand"
                        selectedKeys={dayMonth ? [dayMonth.toString()] : []}
                        onChange={(e) =>
                            handleDayChange(
                                day,
                                e.target.value ? parseInt(e.target.value, 10) : null,
                            )
                        }
                        isDisabled={isDisabled}
                        variant="bordered"
                        classNames={{ label: "text-xs", value: "text-sm" }}
                    >
                        {MONTHS.map((m) => (
                            <SelectItem key={m.value.toString()}>{m.label}</SelectItem>
                        ))}
                    </Select>
                </div>
            ) : (
                <div className="grid grid-cols-3 gap-2">
                    <Select
                        size="sm"
                        label="Volgorde"
                        placeholder="Kies"
                        selectedKeys={ordinal !== null ? [ordinal.toString()] : []}
                        onChange={(e) =>
                            handleWeekdayChange(
                                e.target.value ? parseInt(e.target.value, 10) : null,
                                weekday,
                                weekdayMonth,
                            )
                        }
                        isDisabled={isDisabled}
                        variant="bordered"
                        classNames={{ label: "text-xs", value: "text-sm" }}
                    >
                        {ORDINALS.map((o) => (
                            <SelectItem key={o.value.toString()}>{o.label}</SelectItem>
                        ))}
                    </Select>
                    <Select
                        size="sm"
                        label="Weekdag"
                        placeholder="Kies dag"
                        selectedKeys={weekday ? [weekday.toString()] : []}
                        onChange={(e) =>
                            handleWeekdayChange(
                                ordinal,
                                e.target.value ? parseInt(e.target.value, 10) : null,
                                weekdayMonth,
                            )
                        }
                        isDisabled={isDisabled}
                        variant="bordered"
                        classNames={{ label: "text-xs", value: "text-sm" }}
                    >
                        {WEEKDAYS.map((w) => (
                            <SelectItem key={w.value.toString()}>{w.label}</SelectItem>
                        ))}
                    </Select>
                    <Select
                        size="sm"
                        label="Maand"
                        placeholder="Kies maand"
                        selectedKeys={weekdayMonth ? [weekdayMonth.toString()] : []}
                        onChange={(e) =>
                            handleWeekdayChange(
                                ordinal,
                                weekday,
                                e.target.value ? parseInt(e.target.value, 10) : null,
                            )
                        }
                        isDisabled={isDisabled}
                        variant="bordered"
                        classNames={{ label: "text-xs", value: "text-sm" }}
                    >
                        {MONTHS.map((m) => (
                            <SelectItem key={m.value.toString()}>{m.label}</SelectItem>
                        ))}
                    </Select>
                </div>
            )}

            {preview && (
                <p className="text-neutral-secondary-mid pl-0.5 text-xs italic">
                    {preview}
                </p>
            )}
        </div>
    );
};

export default GeneralMeetingDateInput;
